import {
  type Message,
  type MessageMenuHint,
  type MessageVisualizationImage,
  isMessageMenuHint,
} from "../types";

export const DEFAULT_VISUALIZATION_MENU_HINT: MessageMenuHint = {
  preset: "standard_without_templates",
  exclude_ids: ["message.apply_template", "message.visualisation"],
};

export const DEFAULT_VISUALIZATION_CARD_VARIANT = "gradient_frame";

// Shown when the server does not set `visualization.ui.title` or message `title`.
export const DEFAULT_VISUALIZATION_TITLE = "🌠Visualize It";

export function sanitizeVisualizationDownloadBasename(raw: string): string {
  const slug = (raw.trim() || "visualization").replace(/\s+/g, "_");
  const withoutExt = slug.replace(/\.(png|jpe?g|gif|webp)$/i, "");
  return withoutExt.length > 0 ? withoutExt : "visualization";
}

// Normalized slide list: `images[]` if non-empty, otherwise `[image]`.
export function getVisualizationImages(
  message: Message,
): MessageVisualizationImage[] {
  const viz = message.visualization;
  if (!viz) return [];
  const multi = viz.images?.filter((i) => i?.url?.trim()) ?? [];
  if (multi.length > 0) return multi;
  if (viz.image?.url?.trim()) return [viz.image];
  return [];
}

export function hasRenderableVisualization(message: Message): boolean {
  return getVisualizationImages(message).length > 0;
}

// Preset + exclusions for POST /message_menu when opening the context menu
export function resolveMessageMenuHint(
  message: Message,
): MessageMenuHint | undefined {
  if (isMessageMenuHint(message.menu)) return message.menu;
  if (message.type === "visualization_message") {
    return DEFAULT_VISUALIZATION_MENU_HINT;
  }
  return undefined;
}

// Card title: server `visualization.ui.title` / `title`, else {@link DEFAULT_VISUALIZATION_TITLE}.
export function getVisualizationMessageUi(message: Message): {
  title: string;
  text: string;
  card_variant: string;
} {
  const viz = message.visualization;
  const rawUiText =
    "ui" in (viz ?? {}) && viz?.ui && "text" in viz.ui
      ? (viz.ui as { text?: unknown }).text
      : undefined;
  const uiText = typeof rawUiText === "string" ? rawUiText.trim() : "";
  const fallbackText = message.text?.trim() || message.description?.trim() || "";
  const explicitTitle =
    viz?.ui?.title?.trim() || message.title?.trim() || DEFAULT_VISUALIZATION_TITLE;

  return {
    title: explicitTitle,
    text: uiText || fallbackText,
    card_variant:
      viz?.ui?.card_variant?.trim() || DEFAULT_VISUALIZATION_CARD_VARIANT,
  };
}
